import { WORKFLOWS_NS } from '../../utils/i18n-namespaces';
import { Select, SelectOption, Stack, Tag } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { stringifyValue } from '../../stores/native-cell-value';
import type { ValidBehavior } from '../../stores/utils/behaviors-vocabulary';
import { TableRow, tableStore } from '../../stores/table-store';
import {
  buildClosedBehaviorsCellName,
  buildClosedBehaviorsCellTags,
  formatBehaviorLabelList,
} from './behavior-tag-affordance';
import { SelectPlaceholder } from './select-placeholder';

interface NodeBehaviorsCellProps {
  // value: TableRow['behaviors'];
  onEdit?: (value: TableRow['behaviors']) => void;
  // isEditing?: boolean;
  rowId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnId?: string | number | symbol | any;
  // index?: number;
}

// PSTUDIO-4167: typed against the shared vocabulary, so this map cannot drift
// from it. Labels stay a UI concern, but adding or removing a token in
// `behaviors-vocabulary` without following it here is now a type error — which
// is what keeps a FOURTH copy of the vocabulary from appearing (there were three
// disagreeing copies before that module).
//
// PSTUDIO-4277: labels, descriptions and key order all come from a finalized
// UI text review. Order is deliberate — cross-cutting conditions (field
// state, node structure, channel) first, then asset-type-gated behaviors
// grouped at the bottom, widest asset-type scope to narrowest — and
// `Object.keys` below renders the dropdown in this same order.
const behaviorOptionsMap: Record<ValidBehavior, string> = {
  disable_input: 'Free text disabled',
  xfer_to_agent: 'Transfer to live agent',
  is_collapsible: 'Collapsible asset',
  multiple_choice: 'Multi-select',
  voice_enable_barge_in: 'Interruption allowed',
  inline: 'Inline',
};

const behaviorDescriptionsMap: Partial<
  Record<keyof typeof behaviorOptionsMap, string>
> = {
  disable_input: 'Applicable when an answer is required.',
  xfer_to_agent: 'Applicable to nodes without an asset or next node.',
  is_collapsible:
    'Applicable to Carousel, Date Picker, List Picker and Selection assets.',
  multiple_choice: 'Applicable to List Picker and Selection assets.',
  voice_enable_barge_in: 'Applicable to voice channels.',
  inline: 'Applicable to Web View assets.',
};

// PSTUDIO-4082: an unmapped token SHOWS its raw value instead of drawing an
// empty tag. `behaviorOptionsMap` is a closed six-key vocabulary, but a row can
// still carry a token outside it — a legacy or backend row, or a CSV written
// before PSTUDIO-4167 added a vocabulary to that import. Such a value was
// invisible in the grid AND in the side panel while still being copied with the
// row, written to the CSV download and published to the compiler. Same decision as `arrayCellText`
// (PSTUDIO-4081): a value the user cannot see is a value the user loses. This
// changes display only; nothing about what is stored or published moves.
//
// NOT closed by this change: opening the cell still drops such a token. The DS
// select keeps only options it can find (`setSelectedOptions` filters `value`
// against the rendered `SelectOption`s), so a token with no option is stripped
// from the element's value at mount, without an event, and the next `ps-change`
// persists the row without it. Making the cell readable arguably makes that
// easier to reach, not harder. Fixing it means either rendering unknown tokens
// as extra options or merging them back in `onChange` — both change what an
// edit WRITES, which waits on the product decision about what `none` means
// (PSTUDIO-4082 PR discussion). Tracked separately; do not "tidy" it here.
//
// `hasOwnProperty`, not a bare lookup with `??`: `behaviorOptionsMap` is an
// object literal, so `behaviorOptionsMap['__proto__']` answers
// `Object.prototype` and `['constructor']` the `Object` function. Neither is
// nullish, so `??` would not fall back — `__proto__` renders as an object and
// throws "Objects are not valid as a React child", and `constructor` draws the
// empty chip this fix exists to remove.
//
// Every entrance can deliver such a value, so no guard elsewhere makes this one
// redundant. `convertToBehaviorsArray` shares the SAME `hasOwnProperty` guard
// (via `behaviors-vocabulary`), which is what keeps a prototype key a plain
// string rather than an object — but since PSTUDIO-4167 it deliberately KEEPS
// an unrecognized token instead of dropping it, so passing through it is not
// protection against reaching this lookup. Nor is a legacy or backend row, the
// full-fidelity JSON clipboard envelope, or the CSV parser.
const getBehaviorOptionLabel = (value: string) => {
  return Object.prototype.hasOwnProperty.call(behaviorOptionsMap, value)
    ? behaviorOptionsMap[value as keyof typeof behaviorOptionsMap]
    : value;
};

const getBehaviorOptionDescription = (value: string) => {
  return behaviorDescriptionsMap[value as keyof typeof behaviorOptionsMap];
};

// PSTUDIO-4082: every consumer below treats this value as an array — `.map`,
// `.filter`, `.length` — but `?.` only guards nullish, so a row holding a raw
// string (the desync PSTUDIO-4081 fixed for the other array columns) threw
// `initialValue?.map is not a function` and took the cell's render down with
// it. `coerceRowArrayColumns` deliberately declines to repair a non-string
// value here, so the renderer has to cope. Normalizing once at the read keeps
// arrays and nullish exactly as they were and gives a stray scalar a single
// tag carrying its text.
const normalizeBehaviorTags = (value: unknown): TableRow['behaviors'] => {
  if (Array.isArray(value) || value === null || value === undefined) {
    return value as TableRow['behaviors'];
  }
  return [stringifyValue(value)] as TableRow['behaviors'];
};

export const NodeBehaviorsMultiSelect: React.FC<NodeBehaviorsCellProps> =
  observer(({ onEdit, rowId, columnId }) => {
    const [t, i18n] = useTranslation();
    const [isSelectRendered, setIsSelectRendered] = useState(false);
    const index = tableStore.getRowIndex(rowId);
    const selectRef = useRef<React.ComponentRef<typeof Select>>(null);
    const initialValue = normalizeBehaviorTags(
      tableStore.getValue(rowId, columnId)
    );
    // The closed cell's accessible name: a KEY plus interpolated values, joined
    // for the ACTIVE language rather than with a hardcoded ", ".
    const cellName = buildClosedBehaviorsCellName(
      initialValue,
      getBehaviorOptionLabel,
      (labels) => formatBehaviorLabelList(labels, i18n.language)
    );

    useEffect(() => {
      if (isSelectRendered && selectRef.current) {
        // Use a longer delay to ensure the component is fully rendered
        setTimeout(() => {
          if (
            selectRef.current &&
            typeof selectRef.current.show === 'function'
          ) {
            selectRef.current.show();
            if (selectRef.current.focus) {
              selectRef.current.focus();
            }
          }
        }, 50);
      }
    }, [isSelectRendered]);

    const handleOnEdit = useCallback(
      (values: TableRow['behaviors'], closestCell: HTMLElement | null) => {
        onEdit?.(values);

        if (closestCell) {
          closestCell.focus();
          setTimeout(() => {
            tableStore.isCellBeingEdited = false;
          }, 100);
        }
      },
      [onEdit]
    );

    if (tableStore.isEditing()) {
      if (!isSelectRendered) {
        return (
          <SelectPlaceholder
            testId={`c-editor-table--${columnId}-${index}-select-placeholder`}
            onOpen={() => setIsSelectRendered(true)}
            // PSTUDIO-4229: the wrapper is `role="button"` with an `aria-label`,
            // and an `aria-label` REPLACES the name computed from its contents —
            // so the chips inside it are not part of the cell's accessible name.
            // With the chips' own tab stops gone (see below), naming the values
            // here is what keeps the cell readable. Which key and which values
            // are decided by `buildClosedBehaviorsCellName`, whose docblock says
            // why the phrase is interpolated rather than concatenated and why
            // the FULL list is named instead of the truncated chips.
            ariaLabel={`${t(`${WORKFLOWS_NS}:${cellName.key}`, {
              ...cellName.options,
            })}`}
            style={{ margin: '-4px 0' }}
          >
            <Stack gutter="2xsmall" direction="row">
              <div className="c-editor-table-multiselect-placeholder-tags">
                {buildClosedBehaviorsCellTags(
                  initialValue,
                  getBehaviorOptionLabel
                ).map((tag) => (
                  // PSTUDIO-4229: no `onRemove` and no `tabIndex`. A chip in a
                  // closed cell is a read-only summary — `ps-tag` renders its
                  // remove control only under `removable`, which
                  // `buildClosedBehaviorsCellTags` always sets to false (the
                  // literal type there is a marker, NOT the guard; its docblock
                  // names what the compiler does and does not catch) — and a
                  // chip that does nothing must not take a tab stop (WCAG
                  // 2.4.3): five values meant five empty stops between the cell
                  // and the next control. The values still reach a screen
                  // reader, through the wrapper's `aria-label` above, which is
                  // the element that is actually operable.
                  <Tag
                    key={tag.value}
                    size="small"
                    removable={tag.removable}
                    newVariant="filled"
                    variant="default"
                    minimal
                  >
                    {tag.isOverflowTag ? <span>{tag.label}</span> : tag.label}
                  </Tag>
                ))}
              </div>
            </Stack>
          </SelectPlaceholder>
        );
      }

      return (
        <Select
          ref={selectRef}
          test-id={`c-editor-table--${columnId}-${index}-select`}
          value={initialValue}
          multiple
          maxOptionsVisible={4}
          size="small"
          onclose={(_ev) => {
            setTimeout(() => {
              tableStore.isCellBeingEdited = false;
            }, 100);

            setTimeout(() => {
              if (selectRef.current?.open) return;

              if (isSelectRendered) {
                setIsSelectRendered(false); // Reset to fake select after closing
              }
            }, 2000);
          }}
          onmouseleave={() => {
            if (selectRef.current?.open) return;

            setTimeout(() => {
              setIsSelectRendered(false); // Reset to fake select after closing
            }, 500);
          }}
          variant="outlined"
          onChange={(e) => {
            handleOnEdit(
              e.target.value as TableRow['behaviors'],
              e.target.closest('td')
            );
          }}
        >
          {Object.keys(behaviorOptionsMap).map((value) => (
            <SelectOption
              key={value}
              value={value}
              description={getBehaviorOptionDescription(value)}
            >
              {getBehaviorOptionLabel(value)}
            </SelectOption>
          ))}
        </Select>
      );
    }

    return (
      <div
        className="c-editor-table-multiselect-placeholder-tags"
        style={{ margin: '-4px 0' }}
      >
        <>
          {initialValue &&
            initialValue?.map((value) => {
              return (
                <div className="c-editor-table__tag" key={value}>
                  {getBehaviorOptionLabel(value)}
                </div>
              );
            })}

          {!initialValue && <div className="c-editor-table__tag">None</div>}
        </>
      </div>
    );
  });
